import { memo, useMemo, useState, useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import InlineRenameInput from '@/components/InlineRenameInput'
import { MoreHorizontal, Plus, Minus, Trash2, Copy, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { persistedDocType } from '@/lib/docType'
import { SpreadsheetCard, TimeseriesCard } from '@/components/previews/StaticFilePreview'
import { clearSheetPreviewHeadCache } from '@/components/previews/useSheetPreviewHead'
import { isSpreadsheetFile } from '@/lib/spreadsheetFile'
import { useAppStore } from '@/store'
import { apiService } from '@/service/api'
import type { QueryResult } from '@/service/api'
import { documentLoadQueue } from '@/utils/documentLoadQueue'
import { getPreloadedBlobUrl, cacheDocumentBlob } from './PdfPreview'
import { cleanDocumentName } from '../utils/stripGraphPrefix'
import { parseGeometryWkt, parseLineStringSegments } from '@/service/graph'
import { getDocumentStatus } from '@/utils/documentStatus'
import { DocumentStatus } from '@/components/StatusTag'
import * as pdfjsLib from 'pdfjs-dist'
import '@/lib/pdfWorkerLoader'

/* ──────────────────────────────────────────────────────────────────────────
   Thumbnail cache — module-level blob URL pool with ref counting
   ────────────────────────────────────────────────────────────────────────── */

interface ThumbEntry {
  url: string
  refCount: number
}

const thumbCache = new Map<string, ThumbEntry>()
const thumbInFlight = new Map<string, Promise<string | null>>()

const THUMB_RENDER_WIDTH = 1200
// Keep up to 5 pages worth of thumbnails in memory (~20 entries).
// Evict least-recently-inserted entries with refCount 0 when over limit.
const THUMB_CACHE_MAX = 20

function evictThumbCache(): void {
  if (thumbCache.size <= THUMB_CACHE_MAX) return
  // Evict entries that are no longer mounted (refCount <= 0) first.
  // If that's not enough, evict entries with the lowest refCount.
  for (const [key, entry] of thumbCache) {
    if (entry.refCount <= 0) {
      URL.revokeObjectURL(entry.url)
      thumbCache.delete(key)
      if (thumbCache.size <= THUMB_CACHE_MAX) break
    }
  }
  // If still over limit after evicting unreferenced entries, force-evict the
  // oldest entries regardless of refCount to enforce the hard cap.
  if (thumbCache.size > THUMB_CACHE_MAX) {
    for (const [key, entry] of thumbCache) {
      URL.revokeObjectURL(entry.url)
      thumbCache.delete(key)
      if (thumbCache.size <= THUMB_CACHE_MAX) break
    }
  }
}

// Deduplicate P&ID image downloads — multiple graph docs may share the same source P&ID
const pnidInFlight = new Map<string, Promise<Blob>>()

function getOrDownloadPnidImage(pnidId: string): Promise<Blob> {
  const cached = pnidInFlight.get(pnidId)
  if (cached) return cached
  // Use the same call as the diagram editor: downloadPnidImage with no type
  // param, which returns properties["image"] (the processed image).
  // This ensures the thumbnail base image matches the diagram background.
  const promise = apiService.downloadPnidImage(pnidId)
  pnidInFlight.set(pnidId, promise)
  promise.catch(() => pnidInFlight.delete(pnidId))
  return promise
}

/** Revoke all unused thumbnail blobs. Call on board navigation. */
export function clearThumbnailCache(): void {
  for (const [, entry] of thumbCache) {
    URL.revokeObjectURL(entry.url)
  }
  thumbCache.clear()
  thumbInFlight.clear()
  pnidInFlight.clear()
}

async function renderPdfPageToImage(pdfUrl: string): Promise<string> {
  const pdf = await pdfjsLib.getDocument({
    url: pdfUrl,
    stopAtErrors: false,
    disableRange: true,
    disableStream: true,
  }).promise

  const page = await pdf.getPage(1)
  const unscaledViewport = page.getViewport({ scale: 1 })
  const scale = Math.max(THUMB_RENDER_WIDTH / unscaledViewport.width, 0.5)
  const viewport = page.getViewport({ scale })

  const canvas = document.createElement('canvas')
  canvas.width = viewport.width
  canvas.height = viewport.height
  const ctx = canvas.getContext('2d')!

  // White background so transparent PDFs don't render as black
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  await page.render({ canvasContext: ctx, viewport, canvas }).promise
  pdf.destroy()

  const thumbBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas toBlob failed'))), 'image/png')
  })
  return URL.createObjectURL(thumbBlob)
}

/**
 * Resolve the actual PDF document ID. Graph documents reference the source
 * P&ID/PFD via their `pnid` property — we need that to download the PDF.
 */
function resolveSourceDocId(docId: string, documents: { id: string; type: string; properties: Record<string, unknown> }[]): string {
  const doc = documents.find((d) => d.id === docId)
  const pnid = doc?.properties?.pnid
  if (doc?.type === 'graph' && typeof pnid === 'string' && pnid) {
    return pnid
  }
  return docId
}

function isGraphDocument(docId: string, documents: { id: string; type: string }[]): boolean {
  return documents.find((d) => d.id === docId)?.type === 'graph'
}

// FS-482: the backend is the source of truth for the per-type thumbnail
// annotation palette. `getAnnotationPalette` fetches the mapping once per
// session (cached via the Promise below + the endpoint's 24h
// Cache-Control header) and the client-side composite uses whatever the
// server returns. This guarantees that the server-rendered PNG
// (Strategy 2 in fetchThumbnail) and the client composite (Strategy 3)
// paint each type with the same color — previously the two sides had
// independent constants with inverted assignments (symbol=red here /
// =blue there, `connection` flipped), which was Pegah's red↔blue flip.
//
// The local constants below act as a cold-start fallback so a render
// that fires before the fetch resolves doesn't drift; they MUST mirror
// the backend's `GRAPH_NODE_COLORS` and `DEFAULT_ANNOTATION_COLOR`. If
// the endpoint fails we keep using these — a brief network blip can't
// introduce drift, only an actual backend deploy that changes the
// palette without rebuilding the frontend bundle (rare, observable).
const FALLBACK_ANNOTATION_PALETTE: AnnotationPalette = {
  colors: {
    symbol: '#EF4444',     // red — matches backend GRAPH_NODE_COLORS['symbol']
    tag: '#22C55E',        // green — matches backend GRAPH_NODE_COLORS['tag']
    connection: '#3B82F6', // blue — matches backend GRAPH_NODE_COLORS['connection']
  },
  default: '#3B82F6',      // blue, per FS-482 user call: "if no color specified maybe just have blue"
}

interface AnnotationPalette {
  colors: Record<string, string>
  default: string
}

let annotationPalettePromise: Promise<AnnotationPalette> | null = null

function getAnnotationPalette(): Promise<AnnotationPalette> {
  if (!annotationPalettePromise) {
    annotationPalettePromise = apiService
      .getThumbnailAnnotationPalette()
      .then((r) => ({
        colors: r.colors ?? FALLBACK_ANNOTATION_PALETTE.colors,
        default: r.default || FALLBACK_ANNOTATION_PALETTE.default,
      }))
      .catch((err) => {
        console.warn('[Thumbnail] annotation-color fetch failed; using fallback', err)
        // Reset so a subsequent render gets a fresh chance.
        annotationPalettePromise = null
        return FALLBACK_ANNOTATION_PALETTE
      })
  }
  return annotationPalettePromise
}

/**
 * Generate a thumbnail for graph documents by compositing the P&ID background
 * image with colored bounding box annotations from graph data.
 *
 * Uses the exact same image the diagram editor loads — downloadPnidImage()
 * with no type param, which returns properties["image"]. This guarantees the
 * thumbnail base matches the expanded view. The image is then resized
 * client-side and annotations are drawn with stroke-only outlines to match
 * the SVG rendering in PnidRenderer.
 */
async function fetchGraphThumbnail(graphDocId: string, pnidDocId: string): Promise<string | null> {
  // Download the same P&ID image the diagram editor uses + graph data
  // + the server-defined annotation color, all in parallel. The color
  // fetch is effectively a no-op after the first request (Promise cache
  // + 24h browser cache), but it gates the canvas paint below so we
  // never draw with a color the backend hasn't authorized.
  const [imageBlob, graphData, annotationPalette] = await Promise.all([
    getOrDownloadPnidImage(pnidDocId),
    apiService.getGraphData(graphDocId),
    getAnnotationPalette(),
  ])

  const imageBitmap = await createImageBitmap(imageBlob)
  const fullW = imageBitmap.width
  const fullH = imageBitmap.height

  // Scale down to thumbnail size; annotation coordinates are in the full image space
  const scale = Math.min(THUMB_RENDER_WIDTH / fullW, 1)
  const cw = Math.round(fullW * scale)
  const ch = Math.round(fullH * scale)

  const canvas = document.createElement('canvas')
  canvas.width = cw
  canvas.height = ch
  const ctx = canvas.getContext('2d', { alpha: false })!
  ctx.drawImage(imageBitmap, 0, 0, cw, ch)
  imageBitmap.close()

  // Extract bounding boxes from graph properties and draw colored overlays
  const propsByNode = new Map<string, Record<string, string>>()
  for (const prop of Object.values(graphData.properties)) {
    let map = propsByNode.get(prop.target_id)
    if (!map) {
      map = {}
      propsByNode.set(prop.target_id, map)
    }
    map[prop.key] = prop.value
  }

  // Match graph editor style: stroke outlines only, no fill.
  // The editor uses strokeWidth=8 on the full image; scale proportionally.
  const lineWidth = Math.max(2, Math.round(cw / 150))
  ctx.lineWidth = lineWidth
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'

  // Annotation coordinates are in full-image pixel space; scale to thumbnail
  const s = scale

  for (const nodeId of graphData.nodes) {
    const props = propsByNode.get(nodeId)
    if (!props) continue
    const nodeType = props['type']
    const geometryWkt = props['geometry_wkt']
    if (!nodeType || !geometryWkt) continue

    // FS-482: per-type color from the backend-authored palette; unknown
    // types fall back to the palette's default. See getAnnotationPalette
    // + FALLBACK_ANNOTATION_PALETTE comments above.
    ctx.strokeStyle = annotationPalette.colors[nodeType] ?? annotationPalette.default

    if (geometryWkt.includes('LINESTRING')) {
      // Stroke each sub-segment as its own path. A MULTILINESTRING's disjoint
      // pieces must never be joined end-to-start, or the bridge paints a
      // phantom corner-to-corner diagonal across the thumbnail.
      for (const pairs of parseLineStringSegments(geometryWkt)) {
        ctx.beginPath()
        ctx.moveTo(pairs[0][0] * s, pairs[0][1] * s)
        for (let i = 1; i < pairs.length; i++) {
          ctx.lineTo(pairs[i][0] * s, pairs[i][1] * s)
        }
        ctx.stroke()
      }
    } else {
      const bbox = parseGeometryWkt(geometryWkt)
      if (bbox) {
        ctx.strokeRect(bbox.x * s, bbox.y * s, bbox.width * s, bbox.height * s)
      }
    }
  }

  const thumbBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas toBlob failed'))), 'image/png')
  })
  return URL.createObjectURL(thumbBlob)
}

const PNID_IMAGE_TIMEOUT_MS = 2000

async function fetchPnidImageWithTimeout(docId: string): Promise<string | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), PNID_IMAGE_TIMEOUT_MS)
  try {
    const blob = await apiService.downloadPnidImage(docId, { signal: controller.signal })
    clearTimeout(timer)
    return URL.createObjectURL(blob)
  } catch {
    clearTimeout(timer)
    return null
  }
}

// FS-482: server returns 503 + Retry-After when a graph thumbnail can't be
// generated with annotations yet (extraction in progress / transient PG
// failure). Retry a small number of times before giving up — falling
// through to a raw-image strategy would paint a misleading thumbnail that
// the server's cache then locks in for an hour ("raw is a crime"). The
// per-attempt delay is the server-sent `Retry-After` header (parsed by
// `fetchOrError` and surfaced as `error.response.retryAfterSeconds`), so
// the cadence stays in sync with the backend's `RETRY_AFTER_SECONDS`
// without a hardcoded mirror that could drift.
const THUMBNAIL_FALLBACK_RETRY_DELAY_MS = 5000
const THUMBNAIL_MAX_RETRIES = 2

async function fetchServerThumbnailWithRetry(docId: string): Promise<string | null> {
  for (let attempt = 0; attempt <= THUMBNAIL_MAX_RETRIES; attempt++) {
    try {
      const { blob } = await apiService.getDocumentThumbnail(docId)
      return URL.createObjectURL(blob)
    } catch (err) {
      const response = (err as { response?: { status?: number; retryAfterSeconds?: number } })?.response
      if (response?.status === 503 && attempt < THUMBNAIL_MAX_RETRIES) {
        const delayMs = response.retryAfterSeconds !== undefined
          ? response.retryAfterSeconds * 1000
          : THUMBNAIL_FALLBACK_RETRY_DELAY_MS
        await new Promise((resolve) => setTimeout(resolve, delayMs))
        continue
      }
      return null
    }
  }
  return null
}

async function fetchThumbnail(docId: string, sourceDocId: string, isGraph: boolean, docType?: string): Promise<string | null> {
  // Strategy 1 (P&ID/PFD): try loading the same image the editor uses, with 2s timeout.
  // This ensures the thumbnail matches the editor exactly.
  // Skip for graph docs whose type was resolved to pnid/pfd — they don't have
  // properties["image"] themselves; the server thumbnail endpoint handles them.
  if ((docType === 'pnid' || docType === 'pfd') && !isGraph) {
    const result = await fetchPnidImageWithTimeout(docId)
    if (result) return result
    // Timed out or failed — fall through to server thumbnail
  }

  // Strategy 2: server-generated thumbnail (small PNG, fast)
  // The server generates annotated thumbnails for graph/P&ID docs too,
  // so this single request replaces the heavy client-side composite.
  // Retries on 503 (FS-482) so transient extraction-in-progress doesn't
  // immediately fall through to a raw-rendering strategy.
  const serverResult = await fetchServerThumbnailWithRetry(docId)
  if (serverResult) return serverResult

  // Strategy 3: client-side graph composite (fallback if server thumbnail unavailable)
  if (isGraph || sourceDocId !== docId) {
    const pnidId = sourceDocId !== docId ? sourceDocId : null
    if (pnidId) {
      try {
        const result = await fetchGraphThumbnail(docId, pnidId)
        if (result) return result
      } catch (err) {
        console.warn(`[Thumbnail] Graph composite failed for ${docId}:`, err)
      }
    }
  }

  // FS-482: for graph docs, do NOT fall through to the raw-image strategies
  // below. Strategies 4/5/6 paint the source PDF/image with no annotations,
  // which is the exact "raw thumbnail" failure mode Rafiu flagged — it
  // looks correct until the user notices entities are missing. Return null
  // so the card shows its placeholder until annotations are available.
  if (isGraph) {
    return null
  }

  // Strategy 4: use preloaded blob URL from PdfPreview's cache (avoids duplicate download)
  try {
    const preloadedUrl = await getPreloadedBlobUrl(sourceDocId)
    if (preloadedUrl) {
      return await renderPdfPageToImage(preloadedUrl)
    }
  } catch (err) {
    console.warn(`[Thumbnail] Preloaded PDF render failed for ${sourceDocId}:`, err)
  }

  // Strategy 5: indexed page image (try both source and original IDs)
  for (const id of [sourceDocId, docId]) {
    try {
      const pageBlob = await apiService.getDocumentPage(id, 0)
      return URL.createObjectURL(pageBlob)
    } catch {
      // Not indexed
    }
  }

  // Strategy 6: download the full source document, cache the blob, and render
  try {
    const blob = await apiService.downloadDocument(sourceDocId)
    const blobUrl = URL.createObjectURL(blob)
    if (blob.type.startsWith('image/')) {
      return blobUrl
    }
    cacheDocumentBlob(sourceDocId, blobUrl, 'pdf')
    try {
      return await renderPdfPageToImage(blobUrl)
    } catch (err) {
      console.warn(`[Thumbnail] Fresh PDF render failed for ${sourceDocId}:`, err)
    }
  } catch (err) {
    console.warn(`[Thumbnail] Download failed for ${sourceDocId}:`, err)
  }

  return null
}

/* ──────────────────────────────────────────────────────────────────────────
   Image thumbnail hook — downloads file and renders first page for PDFs
   ────────────────────────────────────────────────────────────────────────── */

const DATA_PREVIEW_TYPES = new Set(['datasheet', 'excel'])

// Types whose card shows the BODY rather than an icon. A transcript is born
// markdown: there is no page to rasterise, but the text itself is the best
// thumbnail it can have — the same reasoning that gives a datasheet a
// MiniTable instead of a spreadsheet glyph (UI-03).
const TEXT_PREVIEW_TYPES = new Set(['transcript'])
// Enough to fill the card at 7px; the rest would be clipped anyway, and the
// body of a long call is not worth downloading in full for a thumbnail.
const TEXT_PREVIEW_CHARS = 1200

// Types where thumbnail fetching is skipped (purely tabular, no visual preview)
// `transcript` (FS-557 User Artifact) is born markdown — there is no page to
// render, so fetching one downloads the body and fails to decode it on every
// card mount. It falls through to its icon like the tabular types (UI-03).
const THUMBNAIL_SKIP_TYPES = new Set(['datasheet', 'excel', 'timeseries', 'transcript'])

// FS-556: while a P&ID/PFD/graph is still being parsed, its thumbnail (the
// server annotation render or the processed page image) doesn't exist yet, so
// fetchThumbnail resolves to null. Under bulk uploads parsing can take many
// minutes, during which the card would otherwise sit on a static placeholder
// that reads as a blank/plain "document". Re-attempt a bounded number of times
// so the diagram fills in on its own once parsing completes — no manual reload.
const THUMB_RETRY_MAX = 20
const THUMB_RETRY_DELAY_MS = 15_000

function useThumbnail(docId?: string, docType?: string) {
  const documents = useAppStore((s) => s.documents)
  const [url, setUrl] = useState<string | null>(() => {
    if (!docId) return null
    return thumbCache.get(`thumb-${docId}`)?.url ?? null
  })
  const [loading, setLoading] = useState(false)
  // Bumped on a timer to re-run the fetch effect while a diagram thumbnail
  // isn't available yet (FS-556). retryCountRef bounds the attempts so a
  // genuinely thumbnail-less card eventually settles on its static placeholder.
  const [retryTick, setRetryTick] = useState(0)
  const retryCountRef = useRef(0)
  const incrementedRef = useRef(false)
  const docIdRef = useRef(docId)
  docIdRef.current = docId

  // Skip download for types that are purely tabular data
  const skip = !docId || (docType && THUMBNAIL_SKIP_TYPES.has(docType))

  // For graph documents, resolve the source P&ID/PFD document ID
  const sourceDocId = useMemo(
    () => (docId ? resolveSourceDocId(docId, documents) : docId),
    [docId, documents]
  )
  const isGraph = useMemo(
    () => docType === 'graph' || (docId ? isGraphDocument(docId, documents) : false),
    [docId, docType, documents]
  )
  // Diagram-bearing cards (P&ID/PFD and their derived graph) get the
  // "still generating" treatment + auto-retry; other types fall straight
  // through to their existing placeholder when no thumbnail is available.
  const isDiagram = isGraph || docType === 'pnid' || docType === 'pfd' || docType === 'graph'

  // FS-556: the "generating" state must be driven by the document's REAL
  // processing status, never by thumbnail absence. A fully-parsed P&ID is
  // status=success (is_indexed / graph_id) yet may render blank if its thumbnail
  // strategy fell through — that is a render concern, not a "still generating"
  // one. Look up the card's own catalog record so only genuinely in-flight
  // statuses (processing/uploading/pending) can drive the generating spinner.
  const docRecord = useMemo(
    () => (docId ? documents.find((d) => d.id === docId) : undefined),
    [docId, documents]
  )
  const status = docRecord ? getDocumentStatus(docRecord) : undefined

  // Reset the retry budget when the target document changes OR its processing
  // status changes. The status dependency lets a card self-heal after a
  // server-side re-process (e.g. a failed parse retried to success) without a
  // board reload — otherwise, once the 20 retries are spent, the card would
  // stay stuck on the static placeholder even though a fresh thumbnail is now
  // available. (Greptile P2, FS-556.)
  useEffect(() => {
    retryCountRef.current = 0
    setRetryTick(0)
  }, [docId, sourceDocId, isGraph, status])

  useEffect(() => {
    incrementedRef.current = false

    if (skip) {
      setUrl(null)
      setLoading(false)
      return
    }

    const key = `thumb-${docId}`
    const cached = thumbCache.get(key)
    if (cached) {
      cached.refCount++
      incrementedRef.current = true
      setUrl(cached.url)
      setLoading(false)
      return
    }

    let mounted = true
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    setLoading(true)

    let promise = thumbInFlight.get(key)
    let isCreator = false
    if (!promise) {
      isCreator = true
      promise = documentLoadQueue.add(key, async () => {
        const existing = thumbCache.get(key)
        if (existing) return existing.url
        try {
          const blobUrl = await fetchThumbnail(docId!, sourceDocId ?? docId!, isGraph, docType)
          if (blobUrl) {
            // Start at refCount 1 so evictThumbCache cannot immediately
            // revoke a freshly inserted entry before the component uses it.
            // The creator skips the .then() increment since this ref is theirs.
            thumbCache.set(key, { url: blobUrl, refCount: 1 })
            evictThumbCache()
          }
          return blobUrl
        } catch {
          return null
        }
      })
      thumbInFlight.set(key, promise)
      promise.finally(() => thumbInFlight.delete(key))
    }

    promise.then((result) => {
      if (!mounted) return
      if (result) {
        const entry = thumbCache.get(key)
        if (entry && !incrementedRef.current) {
          // Creator's ref is already counted in the initial refCount: 1
          if (!isCreator) entry.refCount++
          incrementedRef.current = true
        }
        setUrl(result)
      } else if (isDiagram && retryCountRef.current < THUMB_RETRY_MAX) {
        // FS-556: no thumbnail yet — for a diagram card this almost always
        // means parsing is still in flight. Schedule a bounded re-attempt so
        // the card swaps in the diagram once it's ready, without a reload.
        retryTimer = setTimeout(() => {
          retryCountRef.current += 1
          setRetryTick((t) => t + 1)
        }, THUMB_RETRY_DELAY_MS)
      }
      setLoading(false)
    })

    return () => {
      mounted = false
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [docId, sourceDocId, isGraph, skip, docType, isDiagram, retryTick])

  useEffect(() => {
    return () => {
      const id = docIdRef.current
      if (!id || !incrementedRef.current) return
      const key = `thumb-${id}`
      const entry = thumbCache.get(key)
      if (!entry) return
      entry.refCount = Math.max(0, entry.refCount - 1)
      // Don't revoke/delete on unmount — let entries persist for pagination reuse.
      // clearThumbnailCache() handles full cleanup when switching boards.
    }
  }, [])

  // `generating` is true ONLY while a diagram card is genuinely still being
  // processed (status in {processing, uploading, pending}) AND has no thumbnail
  // yet AND we still expect one (retries not exhausted). It must NEVER key off
  // thumbnail absence alone — a parsed P&ID is status=success and may render
  // blank for thumbnail/render reasons (FS-556); that case falls through to the
  // shimmer/static placeholder, not the spinner. A card whose record hasn't
  // loaded yet (status === undefined) is likewise excluded — no unbacked spinner.
  const generating =
    !url &&
    !skip &&
    isDiagram &&
    retryCountRef.current < THUMB_RETRY_MAX &&
    (status === DocumentStatus.processing ||
      status === DocumentStatus.uploading ||
      status === DocumentStatus.pending)

  return { url, loading, generating }
}

/* ──────────────────────────────────────────────────────────────────────────
   Data preview hook — fetches first rows for tabular documents
   ────────────────────────────────────────────────────────────────────────── */

interface DataPreview {
  columns: string[]
  rows: (string | number | boolean | null)[][]
}

const dataPreviewCache = new Map<string, DataPreview | null>()

async function fetchDataPreview(docId: string): Promise<DataPreview | null> {
  // Get sheets, then try the preview endpoint, then fall back to a SQL query
  let sheetName: string | null = null
  try {
    const sheets = await apiService.getDatasheetSheets(docId)
    if (sheets.sheets.length > 0) {
      sheetName = sheets.sheets[0].table_name

      // Fast preview endpoint
      try {
        const preview: QueryResult = await apiService.getDatasheetPreview(docId, sheetName)
        if (preview.columns.length > 0 && preview.rows.length > 0) {
          return {
            columns: preview.columns.slice(0, 6),
            rows: preview.rows.slice(0, 8).map((r) => r.slice(0, 6)),
          }
        }
      } catch (err) {
        console.warn(`[DataPreview] Preview endpoint failed for ${docId}, trying SQL:`, err)
      }

      // SQL query fallback (mirrors DatasheetPreview component)
      try {
        const result = await apiService.queryDatasheet(
          docId,
          `SELECT * FROM [${sheetName}] LIMIT 8`
        )
        if (result.columns.length > 0 && result.rows.length > 0) {
          return {
            columns: result.columns.slice(0, 6),
            rows: result.rows.slice(0, 8).map((r) => r.slice(0, 6)),
          }
        }
      } catch (err) {
        console.warn(`[DataPreview] SQL query failed for ${docId}:`, err)
      }
    }
  } catch (err) {
    console.warn(`[DataPreview] Sheets fetch failed for ${docId}:`, err)
  }

  return null
}

function useDataPreview(docId?: string, docType?: string): { data: DataPreview | null; loading: boolean } {
  const shouldFetch = !!(docId && docType && DATA_PREVIEW_TYPES.has(docType))

  const [data, setData] = useState<DataPreview | null>(() => {
    if (!docId) return null
    return dataPreviewCache.get(docId) ?? null
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!shouldFetch || !docId) {
      setData(null)
      setLoading(false)
      return
    }

    const cached = dataPreviewCache.get(docId)
    if (cached !== undefined) {
      setData(cached)
      return
    }

    let mounted = true
    setLoading(true)

    // Fetch directly (not through documentLoadQueue) to avoid queue starvation
    // from preloadDocumentBlobs which downloads all files through the same queue
    const run = async () => {
      try {
        const existing = dataPreviewCache.get(docId)
        if (existing !== undefined) return existing

        const result = await fetchDataPreview(docId)
        dataPreviewCache.set(docId, result)
        return result
      } catch {
        return null
      }
    }

    run().then((result) => {
      if (!mounted) return
      setData(result ?? null)
      setLoading(false)
    })

    return () => { mounted = false }
  }, [docId, shouldFetch])

  return { data, loading }
}

export function clearDataPreviewCache(): void {
  dataPreviewCache.clear()
  textPreviewCache.clear()
  clearSheetPreviewHeadCache()
}

const textPreviewCache = new Map<string, string | null>()

/**
 * First page-worth of a born-markdown body, for cards that show their content
 * instead of a glyph. Mirrors useDataPreview: cache first, fetch once, null
 * when unavailable so the caller falls through to its icon.
 */
function useTextPreview(docId?: string, docType?: string): { text: string | null; loading: boolean } {
  const shouldFetch = !!(docId && docType && TEXT_PREVIEW_TYPES.has(docType))

  const [text, setText] = useState<string | null>(() =>
    docId ? (textPreviewCache.get(docId) ?? null) : null,
  )
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!shouldFetch || !docId) {
      setText(null)
      setLoading(false)
      return
    }
    const cached = textPreviewCache.get(docId)
    if (cached !== undefined) {
      setText(cached)
      return
    }

    let mounted = true
    setLoading(true)
    documentLoadQueue
      .add(`text-${docId}`, () => apiService.downloadDocument(docId))
      .then((blob) => (blob as Blob).text())
      .then((body) => {
        const trimmed = body.slice(0, TEXT_PREVIEW_CHARS).trim()
        const value = trimmed.length ? trimmed : null
        textPreviewCache.set(docId, value)
        if (mounted) setText(value)
      })
      .catch(() => {
        // Cached as null so a failed body is not re-fetched on every remount;
        // the card falls through to its icon.
        textPreviewCache.set(docId, null)
        if (mounted) setText(null)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [shouldFetch, docId])

  return { text, loading }
}

/* ──────────────────────────────────────────────────────────────────────────
   SVG fallback icons — used when thumbnails are unavailable
   ────────────────────────────────────────────────────────────────────────── */

const STROKE = 1

const PfdSvg = ({ color }: { color: string }) => (
  <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
    <rect x="16" y="16" width="6" height="6" rx="1" />
    <rect x="2" y="16" width="6" height="6" rx="1" />
    <rect x="9" y="2" width="6" height="6" rx="1" />
    <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3" />
    <path d="M12 12V8" />
  </svg>
)

const DocumentSvg = ({ color }: { color: string }) => (
  <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M10 9H8" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
  </svg>
)

const TimeSeriesSvg = ({ color }: { color: string }) => (
  <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 22h2a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v3" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <circle cx="8" cy="16" r="6" />
    <path d="M9.5 17.5 8 16.25V14" />
  </svg>
)

const GraphSvg = ({ color }: { color: string }) => (
  <svg width="42" height="42" viewBox="0 0 26 26" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13" cy="5" r="3" />
    <circle cx="5" cy="21" r="3" />
    <circle cx="21" cy="21" r="3" />
    <path d="M11 7.5L7 18.5" />
    <path d="M15 7.5L19 18.5" />
    <path d="M8 21h10" />
  </svg>
)

const DatasheetSvg = ({ color }: { color: string }) => (
  <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="1" width="22" height="22" rx="2" />
    <path d="M1 8h22M1 15h22M9 1v22M16 1v22" />
  </svg>
)

const InterviewSvg = ({ color }: { color: string }) => (
  <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
    <rect x="7" y="1" width="10" height="14" rx="5" />
    <path d="M3 13a9 9 0 0 0 18 0" />
    <path d="M12 21v3M8 24h8" />
  </svg>
)

const FallbackSvg = ({ color }: { color: string }) => (
  <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
  </svg>
)

const ExpandSvg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h6v6" />
    <path d="M9 21H3v-6" />
    <path d="M21 3l-7 7" />
    <path d="M3 21l7-7" />
  </svg>
)

/* ──────────────────────────────────────────────────────────────────────────
   Icon + label maps
   ────────────────────────────────────────────────────────────────────────── */

const DOC_TYPE_ICONS: Record<string, typeof PfdSvg> = {
  pnid: PfdSvg,
  pfd: PfdSvg,
  document: DocumentSvg,
  timeseries: TimeSeriesSvg,
  graph: GraphSvg,
  datasheet: DatasheetSvg,
  interview: InterviewSvg,
  // A User Artifact is a call transcript — the same family as an interview,
  // so it takes the same glyph rather than the generic file fallback.
  transcript: InterviewSvg,
  device_config: DocumentSvg,
}

const DOC_TYPE_LABELS: Record<string, string> = {
  pnid: 'P&ID',
  pfd: 'PFD',
  document: 'Document',
  timeseries: 'Time Series',
  graph: 'Graph',
  datasheet: 'Datasheet',
  interview: 'Interview',
  transcript: 'User Artifact',
  device_config: 'Device Config',
}

const ICON_COLOR = '#374151'


/* ──────────────────────────────────────────────────────────────────────────
   Component
   ────────────────────────────────────────────────────────────────────────── */

type DocumentIconProps = {
  documentId?: string
  name?: string
  docType?: string
  width: number
  height: number
  isSelected: boolean
  zoom?: number
  onOpen?: () => void
  onRename?: (newName: string) => void
  /** Kebab → "Remove from Board" — deletes the card from the tldraw canvas. */
  onRemoveFromBoard?: () => void
}

function highlightMatch(text: string, query: string): ReactNode {
  if (!query.trim()) return text
  const lower = text.toLowerCase()
  const q = query.toLowerCase()
  const parts: React.ReactNode[] = []
  let i = 0
  while (i < text.length) {
    const idx = lower.indexOf(q, i)
    if (idx === -1) {
      parts.push(text.slice(i))
      break
    }
    if (idx > i) parts.push(text.slice(i, idx))
    parts.push(<mark key={`hl-${idx}`} className="bg-amber-200 rounded px-0.5 font-medium">{text.slice(idx, idx + q.length)}</mark>)
    i = idx + q.length
  }
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts
}

/**
 * The body as the card's thumbnail. Markdown is shown as written rather than
 * rendered: at 7px a heading and a paragraph are indistinguishable, and the
 * `#`/`**` markers are what make it read as a transcript at a glance.
 */
const MiniText = memo(({ text }: { text: string }) => (
  <div className="w-full h-full overflow-hidden p-1.5">
    <pre className="whitespace-pre-wrap break-words font-sans text-[7px] leading-[10px] text-gray-600 dark:text-gray-300">
      {text}
    </pre>
  </div>
))
MiniText.displayName = 'MiniText'

const MiniTable = memo(({ data }: { data: DataPreview }) => {
  const { columns, rows } = data
  return (
    <div
      className="w-full h-full overflow-hidden p-1"
      data-selectable-table
    >
      <table className="w-full border-collapse text-[7px] leading-[10px] text-gray-600 dark:text-gray-300">
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th key={i} className="border border-gray-200 dark:border-dark-border bg-gray-100 dark:bg-dark-accent px-1 py-0.5 text-left font-semibold truncate max-w-[60px]">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} className="border border-gray-200 dark:border-dark-border px-1 py-0.5 truncate max-w-[60px]">
                  {cell ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
})

// Static previews for unrasterisable files live in components/previews so the
// tag-mapping board can render the identical cards without importing pdf.js.

const DocumentIcon = ({ documentId, name, docType: rawDocType, width, height, isSelected, zoom = 1, onOpen, onRename, onRemoveFromBoard }: DocumentIconProps) => {
  // A `live_stream` anchor (Eventhouse / streamed tags) is PERSISTED as
  // 'timeseries' (see persistedDocType), so after a refresh this component
  // receives 'timeseries' and renders the Time Series card. But shape props
  // deliberately keep the RAW type, so on the first add it arrives as
  // 'live_stream' — which is in neither DOC_TYPE_ICONS nor DOC_TYPE_LABELS and
  // therefore fell back to the generic "File" icon+label until the user
  // reloaded. Normalizing through the same mapper makes the first render
  // identical to the post-refresh one.
  const docType = persistedDocType({ doc_type: rawDocType })
  const { t } = useTranslation('board')
  const { url: thumbUrl, loading: thumbLoading, generating } = useThumbnail(documentId, docType)
  const { data: dataPreview, loading: dataLoading } = useDataPreview(documentId, docType)
  const { text: textPreview, loading: textLoading } = useTextPreview(documentId, docType)
  const IconComponent = DOC_TYPE_ICONS[docType ?? ''] ?? FallbackSvg
  const typeLabel = t(`board:doc_type_labels.${docType ?? ''}`, {
    defaultValue: DOC_TYPE_LABELS[docType ?? ''] ?? t('board:doc_type_labels.file', { defaultValue: 'File' }),
  }) as string
  // Final guard: even if every upstream strip path missed, never let the
  // legacy "P&ID Graph "/"PFD Graph " marker — or, for pnid/pfd-typed
  // rows, the bare "P&ID "/"PFD " marker — land on the card header.
  const displayName = cleanDocumentName(name || '', docType) || 'Untitled'
  const boardFileSearchQuery = useAppStore((s) => s.boardFileSearchQuery)
  const addDocToChatContext = useAppStore((s) => s.addDocToChatContext)
  const removeDocFromChatContext = useAppStore((s) => s.removeDocFromChatContext)
  // Membership flag — drives both the blue border and the kebab item swap
  // (Add → Remove from Chat). Selector returns a primitive so zustand's
  // Object.is check only re-renders this card when the flag actually flips.
  const isInChatContext = useAppStore((s) =>
    documentId ? s.chatContextDocIds.includes(documentId) : false,
  )

  // Manual kebab menu — bypasses Radix Dropdown because tldraw's pointer
  // capture interferes with Radix's outside-click detection. We position
  // the menu via getBoundingClientRect on the trigger and portal it to
  // document.body, then close on click-outside via a window listener.
  const kebabTriggerRef = useRef<HTMLButtonElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })

  const openKebab = () => {
    const rect = kebabTriggerRef.current?.getBoundingClientRect()
    if (rect) {
      // align-end equivalent: align right edge of menu to right edge of trigger
      setMenuPos({ top: rect.bottom + 4, left: rect.right })
    }
    setMenuOpen(true)
  }

  useEffect(() => {
    if (!menuOpen) return
    const onDocPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest('[data-kebab-menu]')) return
      if (target?.closest('[data-kebab-trigger]')) return
      setMenuOpen(false)
    }
    window.addEventListener('pointerdown', onDocPointerDown, true)
    return () => window.removeEventListener('pointerdown', onDocPointerDown, true)
  }, [menuOpen])

  // Inline rename state
  const [isRenaming, setIsRenaming] = useState(false)
  const [editValue, setEditValue] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)

  const nameContent = useMemo(
    () => highlightMatch(displayName, boardFileSearchQuery),
    [displayName, boardFileSearchQuery]
  )

  const isSearchMatch = useMemo(() => {
    if (!boardFileSearchQuery.trim()) return false
    return displayName.toLowerCase().includes(boardFileSearchQuery.toLowerCase())
  }, [displayName, boardFileSearchQuery])

  const clampedZoom = Math.max(zoom, 0.4)
  const headerFontSize = Math.max(4, 10 / clampedZoom)

  const stopAndCall = (handler?: () => void) => (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation()
    e.preventDefault()
    handler?.()
  }

  const isSavingRef = useRef(false)

  const startRenaming = () => {
    isSavingRef.current = false
    setEditValue(displayName)
    setIsRenaming(true)
    setTimeout(() => {
      const el = renameInputRef.current
      if (!el) return
      el.focus()
      el.select()
      // select() places the caret at the end of the value, which scrolls
      // long names so only the trailing portion is visible. Reset scrollLeft
      // so the start of the filename is shown.
      el.scrollLeft = 0
    }, 0)
  }

  const saveRename = () => {
    if (isSavingRef.current) return
    isSavingRef.current = true
    const trimmed = editValue.trim()
    setIsRenaming(false)
    if (trimmed && trimmed !== displayName) {
      onRename?.(trimmed)
    }
  }

  const cancelRename = () => {
    isSavingRef.current = true // prevent onBlur from triggering saveRename
    setIsRenaming(false)
    setEditValue(displayName)
  }

  const borderClasses = isSearchMatch
    ? 'border-amber-400 ring-2 ring-amber-200 shadow-[0_2px_12px_rgba(15,23,42,0.08)]'
    : isInChatContext
      ? 'border-blue-500 shadow-[0_4px_16px_rgba(59,130,246,0.22)] ring-2 ring-blue-400/35'
    : isSelected
      ? 'border-[#0e7490] shadow-[0_4px_16px_rgba(0,122,124,0.18)] ring-2 ring-[#0e7490]/15'
      : 'border-[#e8eaee] dark:border-dark-border shadow-[0_2px_12px_rgba(15,23,42,0.06)] hover:border-[#0e7490]/45 hover:shadow-[0_4px_14px_rgba(15,23,42,0.1)]'

  const isLoading = thumbLoading || dataLoading || textLoading

  let bodyContent: ReactNode
  if (thumbUrl) {
    bodyContent = (
      <img src={thumbUrl} alt="" className="w-full h-full object-contain" draggable={false} />
    )
  } else if (dataPreview) {
    bodyContent = <MiniTable data={dataPreview} />
  } else if (textPreview) {
    bodyContent = <MiniText text={textPreview} />
  } else if (docType === 'timeseries') {
    bodyContent = <TimeseriesCard docId={documentId} />
  } else if (isSpreadsheetFile(name, docType)) {
    bodyContent = <SpreadsheetCard docId={documentId} />
  } else if (generating) {
    // FS-556: a P&ID/PFD/graph card whose diagram isn't ready yet (still
    // parsing — often minutes under bulk uploads). Show an explicit generating
    // state so the card never reads as a blank/plain document; the thumbnail
    // hook auto-retries and swaps in the diagram once parsing completes.
    bodyContent = (
      <div className="flex flex-col items-center justify-center gap-2 text-gray-400 dark:text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="text-[10px] font-medium">
          {t('board:document_preview.generating_diagram', { defaultValue: 'Generating diagram…' })}
        </span>
      </div>
    )
  } else if (isLoading) {
    bodyContent = (
      <div className="w-full h-full bg-gradient-to-br from-gray-100 via-gray-200 to-gray-100 animate-pulse" />
    )
  } else {
    bodyContent = (
      <div className="flex flex-col items-center justify-center gap-1">
        <IconComponent color={ICON_COLOR} />
        <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">{typeLabel}</span>
      </div>
    )
  }

  return (
    <div
      style={{ width, height }}
      className="relative flex flex-col cursor-grab active:cursor-grabbing select-none"
    >
      {/* Floating header — sits above the card, no background */}
      <div className="group/name flex items-center justify-between gap-2 px-1 pb-2 shrink-0">
        {isRenaming ? (
          // Same pill box as display mode (border, padding, font metrics), so
          // the header keeps its exact footprint while renaming — only the
          // name inside swaps for an auto-sizing input.
          <div
            className={`flex items-center gap-1 min-w-0 rounded-lg px-3 py-1.5 shadow-sm border ${isSearchMatch ? 'bg-amber-50 border-amber-300' : 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border'}`}
            style={{ fontSize: `${headerFontSize}px`, lineHeight: `${headerFontSize + 4}px`, maxWidth: '80%' }}
          >
            <InlineRenameInput
              ref={renameInputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation()
                if (e.key === 'Enter') { e.preventDefault(); saveRename() }
                else if (e.key === 'Escape') { e.preventDefault(); cancelRename() }
              }}
              onBlur={saveRename}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="text-gray-700 dark:text-gray-300 font-medium truncate text-left"
              maxLength={255}
            />
          </div>
        ) : (
          // Name + copy live inside ONE pill (like the board-name control) so the
          // copy icon shares the pill's white background instead of floating bare
          // over the drawing behind it.
          <div
            className={`group/pill flex items-center gap-1 min-w-0 rounded-lg px-3 py-1.5 shadow-sm border ${isSearchMatch ? 'bg-amber-50 border-amber-300' : 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border'}`}
            style={{ fontSize: `${headerFontSize}px`, lineHeight: `${headerFontSize + 4}px`, maxWidth: '80%' }}
          >
            <button
              type="button"
              className="text-gray-700 dark:text-gray-300 font-medium truncate min-w-0 text-left hover:text-blue-600 transition-colors cursor-pointer bg-transparent"
              title={t('board:actions.click_to_rename', { defaultValue: 'Click to rename' })}
              onClick={stopAndCall(startRenaming)}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {nameContent}
            </button>
            <button
              type="button"
              className="shrink-0 opacity-0 group-hover/pill:opacity-50 hover:!opacity-100 transition-opacity text-gray-500 dark:text-dark-text-muted cursor-pointer"
              title={t('board:document_preview.click_to_copy', { defaultValue: 'Click to copy filename' })}
              onClick={stopAndCall(() => {
                navigator.clipboard.writeText(displayName)
                toast.success(t('board:toasts.filename_copied', { defaultValue: 'Filename copied' }))
              })}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Copy style={{ width: '1em', height: '1em' }} />
            </button>
          </div>
        )}
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            className="text-gray-500 dark:text-dark-text-muted hover:text-blue-600 cursor-pointer transition-colors p-1.5 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-sm hover:bg-gray-50 dark:hover:bg-dark-muted"
            title={t('board:document_preview.open_in_board_review', { defaultValue: 'Open in-board review' })}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onOpen?.()
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <ExpandSvg />
          </button>
          {/* Kebab menu — manual implementation (no Radix Dropdown). tldraw's
              pointer-event capture inside HTMLContainer conflicts with Radix's
              outside-click + portal logic, so we drive the menu state directly
              and portal a plain absolute-positioned div to document.body. */}
          <button
            ref={kebabTriggerRef}
            type="button"
            data-kebab-trigger
            className="text-gray-500 dark:text-dark-text-muted hover:text-blue-600 cursor-pointer transition-colors p-1.5 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-sm hover:bg-gray-50 dark:hover:bg-dark-muted"
            title={t('board:document_preview.more_actions', { defaultValue: 'More actions' })}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              if (menuOpen) setMenuOpen(false)
              else openKebab()
            }}
          >
            <MoreHorizontal className="w-5 h-5" />
          </button>
        </div>
        {/* Tooltip on hover — must be inside group/name for group-hover to work */}
        {/* Render only when an upstream name was actually supplied (don't pop a tooltip for the "Untitled" placeholder), but show the cleaned displayName so the legacy prefix doesn't leak through. */}
        {name && (
          <div
            className="absolute left-1/2 top-0 hidden group-hover/name:block pointer-events-none z-50"
            style={{ transform: `translateX(-50%) translateY(-100%) scale(${1 / clampedZoom})`, transformOrigin: 'bottom center' }}
          >
            <div className="bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-normal shadow-md max-w-[300px] break-words">
              {displayName}
            </div>
          </div>
        )}
      </div>

      {/* Thumbnail card — always visible, header floats above it separately */}
      <div className={`flex-1 min-h-0 rounded-xl overflow-hidden border transition-all bg-white dark:bg-dark-card flex flex-col ${borderClasses}`}>
        <div
          className="flex-1 min-h-0 flex items-center justify-center overflow-hidden"
          onDoubleClick={stopAndCall(onOpen)}
        >
          {bodyContent}
        </div>
      </div>
      {menuOpen && createPortal(
        <div
          data-kebab-menu
          className="fixed z-[1000] min-w-[200px] rounded-lg border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card shadow-lg p-1"
          style={{ top: menuPos.top, left: menuPos.left, transform: 'translateX(-100%)' }}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {isInChatContext ? (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-destructive/10 text-destructive"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setMenuOpen(false)
                if (!documentId) return
                removeDocFromChatContext(documentId)
              }}
            >
              <Minus className="w-4 h-4" />
              {t('board:document_preview.remove_from_chat', { defaultValue: 'Remove from Chat' })}
            </button>
          ) : (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-dark-muted text-foreground"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setMenuOpen(false)
                if (!documentId) {
                  toast.error(
                    t('board:document_preview.add_to_chat_error', {
                      defaultValue: 'Cannot add to chat — document id missing',
                    }),
                  )
                  return
                }
                addDocToChatContext(documentId)
              }}
            >
              <Plus className="w-4 h-4" />
              {t('board:document_preview.add_to_chat', { defaultValue: 'Add to Chat' })}
            </button>
          )}
          <button
            type="button"
            className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-destructive/10 text-destructive"
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              setMenuOpen(false)
              onRemoveFromBoard?.()
            }}
          >
            <Trash2 className="w-4 h-4" />
            {t('board:document_preview.remove_from_board_action', { defaultValue: 'Remove from Board' })}
          </button>
        </div>,
        document.body,
      )}
    </div>
  )
}

export default memo(DocumentIcon)
